import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, KeyRound, Shield, ExternalLink, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * Admin tool: Private Park Host Onboarding (JSON diff generator).
 *
 * Why this page exists
 * --------------------
 * Adding a new private-park host today is a ~5-minute hand-edit to
 * `/app/frontend/src/data/privateParks.json` — easy to get wrong, easy
 * to forget a field, and easy to slip in a bad slug. This tool
 * captures host info in a structured form, validates it client-side,
 * and prints a ready-to-paste JSON object the operator can drop into
 * the privateParks.json array in one move.
 *
 * Auth model
 * ----------
 * No backend writes happen here — the form output is copy-paste only.
 * To keep the page out of public hands we still gate it behind the
 * same `X-Admin-Token` already cached in sessionStorage by the
 * AdminSubmissions page. If the token isn't set the operator is told
 * to unlock via /admin/submissions first (single source of truth for
 * the admin gate). Page is noindex'd.
 */

const SESSION_KEY = "pg_admin_token";

const STATE_LIST = [
  "AL","AK","AZ","AR","CA","CO","CT","DC","DE","FL","GA","HI","IA","ID","IL","IN",
  "KS","KY","LA","MA","MD","ME","MI","MN","MO","MS","MT","NC","ND","NE","NH","NJ",
  "NM","NV","NY","OH","OK","OR","PA","PR","RI","SC","SD","TN","TX","UT","VA","VT",
  "WA","WI","WV","WY",
];

// Mirrors src/data/privateParks.js + scripts/prerender.js slug logic
// exactly so the generated slug matches what the rest of the codebase
// will compute. Strips diacritics, drops apostrophes/periods, then
// collapses non-alphanumerics to single hyphens.
const kebab = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[''.]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const splitLines = (s) =>
  String(s || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const Field = ({ label, hint, children, required }) => (
  <label className="flex flex-col gap-1.5 text-sm">
    <span className="font-body font-semibold text-[#2d2a26]">
      {label}
      {required && <span className="text-[#B45309]"> *</span>}
    </span>
    {hint && <span className="text-xs text-[#8a857d]">{hint}</span>}
    {children}
  </label>
);

const inputClass =
  "w-full px-3 py-2 rounded-lg border border-[#E5DFD3] bg-white font-body text-sm text-[#1A1A0F] focus:outline-none focus:border-[#4A7C59]";

const AdminPrivateParkOnboard = () => {
  useNoIndex();

  const [token] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) || "";
    } catch {
      return "";
    }
  });

  // Host + listing fields ---------------------------------------------
  const [form, setForm] = useState({
    name: "",
    tagline: "",
    address: "",
    city: "",
    state: "FL",
    foundingHost: true,
    photos: "",
    hostName: "",
    headshot: "",
    phone: "",
    email: "",
    bio: "",
    respondsIn: "Usually responds within 1 hour",
    firstDog: 15,
    additional: 7.5,
    maxDogs: 3,
    maxHours: 4,
    about: "",
    highlights: [
      { emoji: "🔒", label: "Fully Fenced", detail: "Secure yard so your dog can play off-leash safely" },
    ],
    amenities: "",
    availabilityHeadline: "Open daily 9:00 AM – 8:00 PM",
    sameDay: true,
    rules: [
      "Entry is not permitted without a pre-paid booking reservation",
      "Clean up after your dog",
      "No aggressive dogs",
    ].join("\n"),
    seoTitle: "",
    seoDescription: "",
  });

  const set = (key) => (e) => {
    const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const setHighlight = (i, key, value) =>
    setForm((f) => ({
      ...f,
      highlights: f.highlights.map((h, idx) => (idx === i ? { ...h, [key]: value } : h)),
    }));

  const addHighlight = () =>
    setForm((f) => ({
      ...f,
      highlights: [...f.highlights, { emoji: "✨", label: "", detail: "" }],
    }));

  const removeHighlight = (i) =>
    setForm((f) => ({
      ...f,
      highlights: f.highlights.filter((_, idx) => idx !== i),
    }));

  // Derived ----------------------------------------------------------
  const slug = useMemo(() => {
    const nameSlug = kebab(form.name);
    const citySlug = kebab(form.city);
    if (!nameSlug || !citySlug) return "";
    // Match the existing convention: "<host>-fenced-yard-<city-base>"
    // e.g. "demarcos-fenced-yard-mount-prospect". We keep the suffix
    // configurable indirectly via the host's first-name choice — the
    // operator can hand-edit the output if a different shape is needed.
    return `${nameSlug.replace(/-fully-fenced-yard$/, "")}-fenced-yard-${citySlug}`;
  }, [form.name, form.city]);

  const citySlug = useMemo(() => {
    const c = kebab(form.city);
    return c && form.state ? `${c}-${form.state.toLowerCase()}` : "";
  }, [form.city, form.state]);

  const phoneRaw = useMemo(() => {
    const digits = String(form.phone || "").replace(/\D/g, "");
    if (!digits) return "";
    return digits.length === 10 ? `+1${digits}` : `+${digits}`;
  }, [form.phone]);

  // Output preview --------------------------------------------------
  const listingJs = useMemo(() => {
    const photos = splitLines(form.photos);
    const amenities = String(form.amenities || "")
      .split(/[,\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const rules = splitLines(form.rules);

    const seoTitle =
      form.seoTitle ||
      `${form.name} — Private Dog Park in ${form.city}, ${form.state} | Pawground`;
    const seoDescription =
      form.seoDescription ||
      `Book a private, fully fenced yard for your dogs in ${form.city}, ${form.state}. ` +
        `$${form.firstDog}/hr first dog, $${form.additional}/hr each additional. No membership required.`;

    // JSON (not a JS literal) so it pastes straight into privateParks.json.
    const entry = {
      slug,
      name: form.name,
      tagline: form.tagline,
      address: form.address,
      city: form.city,
      state: form.state,
      citySlug,
      foundingHost: !!form.foundingHost,
      rating: 5.0,
      reviewCount: 0,
      photos,
      host: {
        name: form.hostName,
        headshot: form.headshot,
        phone: form.phone,
        phoneRaw,
        email: form.email,
        bio: form.bio,
        respondsIn: form.respondsIn,
      },
      pricing: {
        firstDogPerHour: Number(form.firstDog) || 0,
        additionalDogPerHour: Number(form.additional) || 0,
        maxDogs: Number(form.maxDogs) || 0,
        maxHours: Number(form.maxHours) || 0,
      },
      about: form.about,
      highlights: form.highlights.map(({ emoji, label, detail }) => ({ emoji, label, detail })),
      amenities,
      availability: { headline: form.availabilityHeadline, sameDay: !!form.sameDay },
      rules,
      reviews: [],
      seo: { title: seoTitle, description: seoDescription },
    };
    return JSON.stringify(entry, null, 2)
      .split("\n")
      .map((line) => `  ${line}`)
      .join("\n") + ",";
  }, [form, slug, citySlug, phoneRaw]);

  // Pre-flight: is the city already known? If not, the operator may
  // need to add the row to cities.templated.json so the listing's
  // breadcrumbs and city-page redirects work cleanly.
  const cityHint = useMemo(() => {
    if (!form.city || !form.state) return "";
    return (
      `Verify ["${form.city}","${form.state}"] exists in ` +
      `src/data/cities.templated.json — if missing, add it so the ` +
      `breadcrumb "${form.city}" link resolves.`
    );
  }, [form.city, form.state]);

  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(listingJs);
      setCopied(true);
      toast.success("Copied — paste into privateParks.json");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  // ----- Gate: require admin token in sessionStorage -----------------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-[#E5DFD3] rounded-2xl p-8 text-center shadow-sm">
          <Shield className="w-10 h-10 mx-auto mb-3 text-[#4A7C59]" />
          <h1 className="font-heading text-2xl text-[#1A1A0F] mb-2">Admin only</h1>
          <p className="font-body text-sm text-[#6b6560] mb-5">
            Unlock the admin gate first, then return here. The same X-Admin-Token used by
            the moderation dashboard authenticates this tool.
          </p>
          <Link
            to="/admin/submissions"
            data-testid="admin-onboard-unlock-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4A7C59] text-white font-body font-semibold text-sm hover:bg-[#3d6649] transition-colors"
          >
            <KeyRound className="w-4 h-4" />
            Open Admin Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#FAF7F2] py-10 px-4"
      data-testid="admin-private-park-onboard-page"
    >
      <div className="max-w-6xl mx-auto">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-[0.18em] text-[#4A7C59] font-semibold mb-1">
            Admin Tool
          </p>
          <h1 className="font-heading text-3xl md:text-4xl text-[#1A1A0F] mb-1">
            Private Park Host Onboarding
          </h1>
          <p className="font-body text-sm text-[#6b6560]">
            Fill in the host details below. The tool generates a ready-to-paste JSON
            object you drop into{" "}
            <code className="px-1 py-0.5 bg-white border border-[#E5DFD3] rounded text-[12px]">
              /app/frontend/src/data/privateParks.json
            </code>{" "}
            inside the top-level array.
          </p>
        </header>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* ----- Form panel ----- */}
          <div className="bg-white border border-[#E5DFD3] rounded-2xl p-6 shadow-sm space-y-5">
            <h2 className="font-heading text-xl text-[#1A1A0F]">Listing details</h2>

            <Field label="Listing name" required hint="e.g. 'Jennifer's Fully Fenced Yard'">
              <input
                data-testid="onboard-input-name"
                className={inputClass}
                value={form.name}
                onChange={set("name")}
              />
            </Field>
            <Field label="Tagline" hint="One-liner under the title">
              <input
                data-testid="onboard-input-tagline"
                className={inputClass}
                value={form.tagline}
                onChange={set("tagline")}
              />
            </Field>
            <Field label="Full address" required>
              <input
                data-testid="onboard-input-address"
                className={inputClass}
                value={form.address}
                onChange={set("address")}
                placeholder="123 Main St, Mount Prospect, IL 60056"
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Field label="City" required>
                  <input
                    data-testid="onboard-input-city"
                    className={inputClass}
                    value={form.city}
                    onChange={set("city")}
                  />
                </Field>
              </div>
              <Field label="State" required>
                <select
                  data-testid="onboard-input-state"
                  className={inputClass}
                  value={form.state}
                  onChange={set("state")}
                >
                  {STATE_LIST.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="onboard-input-founding"
                checked={form.foundingHost}
                onChange={set("foundingHost")}
              />
              <span className="font-body text-[#2d2a26]">Founding Host (shows badge)</span>
            </label>

            <Field
              label="Photo URLs"
              required
              hint="One URL per line. First photo becomes the cover + og:image preview."
            >
              <textarea
                data-testid="onboard-input-photos"
                className={inputClass}
                rows={6}
                value={form.photos}
                onChange={set("photos")}
                placeholder="https://customer-assets.emergentagent.com/.../yard-1.jpg"
              />
            </Field>

            <h2 className="font-heading text-xl text-[#1A1A0F] pt-3 border-t border-[#E5DFD3]">
              Host info
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Host first name" required>
                <input
                  data-testid="onboard-input-host-name"
                  className={inputClass}
                  value={form.hostName}
                  onChange={set("hostName")}
                />
              </Field>
              <Field label="Headshot URL" hint="Optional but recommended">
                <input
                  data-testid="onboard-input-host-headshot"
                  className={inputClass}
                  value={form.headshot}
                  onChange={set("headshot")}
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" required hint="Auto-formats phoneRaw">
                <input
                  data-testid="onboard-input-host-phone"
                  className={inputClass}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(312) 771-9397"
                />
              </Field>
              <Field label="Email" required>
                <input
                  data-testid="onboard-input-host-email"
                  className={inputClass}
                  value={form.email}
                  onChange={set("email")}
                  placeholder="host@example.com"
                />
              </Field>
            </div>
            <Field label="Host bio" hint="2-3 sentences shown on the listing">
              <textarea
                data-testid="onboard-input-host-bio"
                className={inputClass}
                rows={3}
                value={form.bio}
                onChange={set("bio")}
              />
            </Field>
            <Field label="Responds in">
              <input
                data-testid="onboard-input-host-responds"
                className={inputClass}
                value={form.respondsIn}
                onChange={set("respondsIn")}
              />
            </Field>

            <h2 className="font-heading text-xl text-[#1A1A0F] pt-3 border-t border-[#E5DFD3]">
              Pricing
            </h2>
            <div className="grid grid-cols-4 gap-3">
              <Field label="$ / 1st dog / hr">
                <input
                  type="number"
                  data-testid="onboard-input-first-dog"
                  className={inputClass}
                  value={form.firstDog}
                  onChange={set("firstDog")}
                />
              </Field>
              <Field label="$ / extra dog / hr">
                <input
                  type="number"
                  step="0.5"
                  data-testid="onboard-input-extra-dog"
                  className={inputClass}
                  value={form.additional}
                  onChange={set("additional")}
                />
              </Field>
              <Field label="Max dogs">
                <input
                  type="number"
                  data-testid="onboard-input-max-dogs"
                  className={inputClass}
                  value={form.maxDogs}
                  onChange={set("maxDogs")}
                />
              </Field>
              <Field label="Max hours">
                <input
                  type="number"
                  data-testid="onboard-input-max-hours"
                  className={inputClass}
                  value={form.maxHours}
                  onChange={set("maxHours")}
                />
              </Field>
            </div>

            <h2 className="font-heading text-xl text-[#1A1A0F] pt-3 border-t border-[#E5DFD3]">
              The yard
            </h2>
            <Field label="About" required hint="Paragraph under the highlights">
              <textarea
                data-testid="onboard-input-about"
                className={inputClass}
                rows={3}
                value={form.about}
                onChange={set("about")}
              />
            </Field>

            <Field label="Highlights" hint="Add 3-4. Emoji, label, detail.">
              <div className="space-y-2">
                {form.highlights.map((h, i) => (
                  <div
                    key={i}
                    className="flex gap-2 items-start"
                    data-testid={`onboard-highlight-row-${i}`}
                  >
                    <input
                      className={`${inputClass} w-14 text-center`}
                      value={h.emoji}
                      onChange={(e) => setHighlight(i, "emoji", e.target.value)}
                      maxLength={4}
                      aria-label={`Highlight ${i + 1} emoji`}
                    />
                    <input
                      className={`${inputClass} w-1/3`}
                      placeholder="Label"
                      value={h.label}
                      onChange={(e) => setHighlight(i, "label", e.target.value)}
                      aria-label={`Highlight ${i + 1} label`}
                    />
                    <input
                      className={`${inputClass} flex-1`}
                      placeholder="Detail"
                      value={h.detail}
                      onChange={(e) => setHighlight(i, "detail", e.target.value)}
                      aria-label={`Highlight ${i + 1} detail`}
                    />
                    <button
                      type="button"
                      onClick={() => removeHighlight(i)}
                      className="p-2 text-[#B91C1C] hover:bg-red-50 rounded"
                      data-testid={`onboard-highlight-remove-${i}`}
                      aria-label="Remove highlight"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addHighlight}
                  data-testid="onboard-add-highlight"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-[#E5DFD3] text-[#4A7C59] hover:bg-[#F0EAE0]"
                >
                  <Plus className="w-3.5 h-3.5" /> Add highlight
                </button>
              </div>
            </Field>

            <Field label="Amenities" hint="Comma- or newline-separated">
              <textarea
                data-testid="onboard-input-amenities"
                className={inputClass}
                rows={3}
                value={form.amenities}
                onChange={set("amenities")}
                placeholder="Fully Fenced, Agility Equipment, Dog Toys, ..."
              />
            </Field>

            <div className="grid grid-cols-1 gap-3">
              <Field label="Availability headline" required>
                <input
                  data-testid="onboard-input-availability"
                  className={inputClass}
                  value={form.availabilityHeadline}
                  onChange={set("availabilityHeadline")}
                />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="onboard-input-same-day"
                  checked={form.sameDay}
                  onChange={set("sameDay")}
                />
                <span className="font-body text-[#2d2a26]">Same-day bookings allowed</span>
              </label>
            </div>

            <Field label="Rules" hint="One per line">
              <textarea
                data-testid="onboard-input-rules"
                className={inputClass}
                rows={4}
                value={form.rules}
                onChange={set("rules")}
              />
            </Field>

            <h2 className="font-heading text-xl text-[#1A1A0F] pt-3 border-t border-[#E5DFD3]">
              SEO overrides{" "}
              <span className="text-xs font-body font-normal text-[#8a857d]">
                (auto-generated if blank)
              </span>
            </h2>
            <Field label="Page title">
              <input
                data-testid="onboard-input-seo-title"
                className={inputClass}
                value={form.seoTitle}
                onChange={set("seoTitle")}
              />
            </Field>
            <Field label="Meta description">
              <textarea
                data-testid="onboard-input-seo-desc"
                className={inputClass}
                rows={2}
                value={form.seoDescription}
                onChange={set("seoDescription")}
              />
            </Field>
          </div>

          {/* ----- Preview / output panel ----- */}
          <div className="space-y-4">
            <div className="bg-white border border-[#E5DFD3] rounded-2xl p-5 shadow-sm">
              <h2 className="font-heading text-lg text-[#1A1A0F] mb-2">Derived fields</h2>
              <dl className="grid grid-cols-3 gap-2 text-sm">
                <dt className="font-semibold text-[#6b6560]">Slug</dt>
                <dd
                  className="col-span-2 font-mono text-[12px] text-[#1A1A0F]"
                  data-testid="onboard-derived-slug"
                >
                  {slug || <em className="text-[#8a857d]">— (fill name + city)</em>}
                </dd>
                <dt className="font-semibold text-[#6b6560]">citySlug</dt>
                <dd
                  className="col-span-2 font-mono text-[12px] text-[#1A1A0F]"
                  data-testid="onboard-derived-cityslug"
                >
                  {citySlug || <em className="text-[#8a857d]">— (fill city + state)</em>}
                </dd>
                <dt className="font-semibold text-[#6b6560]">phoneRaw</dt>
                <dd className="col-span-2 font-mono text-[12px] text-[#1A1A0F]">
                  {phoneRaw || <em className="text-[#8a857d]">—</em>}
                </dd>
              </dl>
              {cityHint && (
                <p className="mt-3 text-xs text-[#8a857d] border-t border-[#E5DFD3] pt-3">
                  ⚠️ {cityHint}
                </p>
              )}
            </div>

            <div className="bg-white border border-[#E5DFD3] rounded-2xl shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5DFD3]">
                <h2 className="font-heading text-lg text-[#1A1A0F]">JSON output</h2>
                <button
                  type="button"
                  onClick={handleCopy}
                  data-testid="onboard-copy-output"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-[#4A7C59] text-white hover:bg-[#3d6649]"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </>
                  )}
                </button>
              </div>
              <pre
                data-testid="onboard-output-preview"
                className="px-5 py-4 overflow-auto text-[12px] leading-relaxed font-mono text-[#1A1A0F] bg-[#FCFBF8] rounded-b-2xl max-h-[70vh]"
              >
                {listingJs}
              </pre>
            </div>

            <div className="bg-[#FFF7E6] border border-[#FDE68A] rounded-xl p-4 text-sm font-body text-[#92400E]">
              <p className="font-semibold mb-1">After pasting:</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>
                  Add the new entry inside the top-level array in{" "}
                  <code>src/data/privateParks.json</code> (drop the trailing comma if it is the last entry).
                </li>
                <li>
                  If <code>{citySlug || "city-slug"}</code> isn&apos;t in{" "}
                  <code>cities.featured.json</code>, add{" "}
                  <code>{`["${form.city || "City"}","${form.state}"]`}</code> to{" "}
                  <code>cities.templated.json</code>.
                </li>
                <li>
                  Commit + redeploy. The <code>prerender</code> step will bake the new
                  listing&apos;s HTML + og:image at build time automatically.
                </li>
              </ol>
              <a
                href="/admin/submissions"
                className="inline-flex items-center gap-1 mt-3 text-[#92400E] hover:underline font-semibold"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Back to admin dashboard
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPrivateParkOnboard;
